import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function Spotlights() {
  const {
    t
  } = useLanguage();
  return <section className="py-20 md:py-32 relative z-10 bg-surface/20 border-y border-white/5" data-id="element-239">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 space-y-20 md:space-y-32" data-id="element-240">
        {/* Finance Spotlight */}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center" data-id="element-241">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="order-1" data-id="element-242">
            <div className="text-accent text-[13px] md:text-sm font-medium tracking-wide uppercase mb-3 md:mb-4" data-id="element-243">
              {t('spotlights.finance.badge')}
            </div>
            <h2 className="text-[40px] leading-[1.05] md:text-5xl font-display font-medium mb-4 md:mb-6" data-id="element-244">
              {t('spotlights.finance.title')}
            </h2>
            <p className="text-white/60 text-[18px] leading-[1.5] md:text-lg font-light mb-8" data-id="element-245">
              {t('spotlights.finance.desc')}
            </p>
            <button className="group flex items-center gap-2 text-white font-medium hover:text-accent transition-colors" data-id="element-246">
              {t('spotlights.finance.cta')}{' '}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-247" />
            </button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="order-2 h-[340px] lg:h-[600px] rounded-[2rem] glass-panel border border-white/10 relative overflow-hidden" data-id="element-248">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.05),transparent_70%)]" data-id="element-249" />
            {/* Abstract Finance Graphic */}
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-250">
              <div className="w-72 h-72 border border-white/5 rounded-full relative flex items-center justify-center" data-id="element-251">
                <div className="absolute w-full h-full border border-white/5 rounded-full scale-75" data-id="element-252" />
                <div className="absolute w-full h-full border border-white/5 rounded-full scale-50" data-id="element-253" />
                <div className="absolute top-1/2 left-0 w-full h-px bg-white/5" data-id="element-254" />
                <div className="absolute top-0 left-1/2 w-px h-full bg-white/5" data-id="element-255" />
                <svg className="absolute inset-0 w-full h-full text-accent" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-256">
                  <defs data-id="element-257">
                    <linearGradient id="finance-grad" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-258">
                      <stop offset="0%" stopColor="rgba(255,106,0,0)" data-id="element-259" />
                      <stop offset="50%" stopColor="rgba(255,106,0,1)" data-id="element-260" />
                      <stop offset="100%" stopColor="rgba(255,106,0,0)" data-id="element-261" />
                    </linearGradient>
                  </defs>
                  <path d="M0,80 Q25,70 50,40 T100,20" fill="none" stroke="url(#finance-grad)" strokeWidth="1.5" data-id="element-262" />
                  <circle cx="50" cy="40" r="2" fill="#FF6A00" className="animate-pulse" data-id="element-263" />
                  <circle cx="100" cy="20" r="2" fill="#FF6A00" data-id="element-264" />
                </svg>
              </div>
            </div>
          </motion.div>
        </div>

        {/* AI Spotlight */}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center" data-id="element-265">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="order-1 lg:order-2" data-id="element-266">
            <div className="text-accent text-[13px] md:text-sm font-medium tracking-wide uppercase mb-3 md:mb-4" data-id="element-267">
              {t('spotlights.ai.badge')}
            </div>
            <h2 className="text-[40px] leading-[1.05] md:text-5xl font-display font-medium mb-4 md:mb-6" data-id="element-268">
              {t('spotlights.ai.title')}
            </h2>
            <p className="text-white/60 text-[18px] leading-[1.5] md:text-lg font-light mb-8" data-id="element-269">
              {t('spotlights.ai.desc')}
            </p>
            <button className="group flex items-center gap-2 text-white font-medium hover:text-accent transition-colors" data-id="element-270">
              {t('spotlights.ai.cta')}{' '}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-271" />
            </button>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="order-2 lg:order-1 h-[340px] lg:h-[600px] rounded-[2rem] glass-panel border border-white/10 relative overflow-hidden" data-id="element-272">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,106,0,0.08),transparent_70%)]" data-id="element-273" />
            {/* Abstract AI Graphic */}
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-274">
              <div className="relative w-64 h-64 scale-75 md:scale-100" data-id="element-275">
                <div className="absolute inset-0 border border-white/10 rounded-full animate-[spin_20s_linear_infinite]" data-id="element-276" />
                <div className="absolute inset-4 border border-white/10 rounded-full animate-[spin_15s_linear_infinite_reverse]" data-id="element-277" />
                <div className="absolute inset-8 border border-white/10 rounded-full animate-[spin_10s_linear_infinite]" data-id="element-278" />

                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-accent/20 rounded-full blur-xl animate-pulse" data-id="element-279" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-accent/40 rounded-full blur-md" data-id="element-280" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_rgba(255,255,255,1)]" data-id="element-281" />

                {/* Orbital Nodes */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-accent rounded-full" data-id="element-282" />
                <div className="absolute bottom-4 left-4 w-1.5 h-1.5 bg-white/50 rounded-full" data-id="element-283" />
                <div className="absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white/80 rounded-full" data-id="element-284" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}